import React from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import {
  ArrowLeft, ArrowRight, Loader2, Sparkles, X,
} from 'lucide-react';

import { STEPS, COUNTRIES_BY_PATH } from './qualification/constants';
import { useQualificationForm } from './qualification/useQualificationForm';
import { ContactStep } from './qualification/ContactStep';
import { PathStep } from './qualification/PathStep';
import { CountriesStep } from './qualification/CountriesStep';
import { BudgetStep } from './qualification/BudgetStep';
import { TimelineStep } from './qualification/TimelineStep';
import { SuccessStep } from './qualification/SuccessStep';

/**
 * QualificationModal — Form 2 of the VSL Gate.
 * Multi-step qualification form that captures contact + preferences and
 * upserts a partial lead into the CRM as the user progresses (progressive
 * save). On submit, the assigned agent is returned along with Calendly CTA.
 *
 * Structural note (★ Round 2 qualità):
 *   • step JSX → `./qualification/*Step.jsx`
 *   • logica (hydration, salvataggio progressivo, navigazione+pixel, submit)
 *     → `./qualification/useQualificationForm.js`
 *   Questo file è ora SOLO orchestrazione + layout del dialog.
 */
export function QualificationModal({ open, onOpenChange, ctaSource = 'unknown' }) {
  const {
    step, currentStepKey, config, loadingCfg, submitting, error, result,
    form, setForm, next, back, canContinue, budgetOptions, toggleCountry, submit,
  } = useQualificationForm({ open, ctaSource });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden bg-[var(--surface-1)] border-[var(--gp-gold)]/30"
        data-testid="qualification-modal"
      >
        {/* Header strip */}
        <div className="px-5 md:px-7 py-4 bg-[hsl(var(--primary))] text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--gp-gold-soft)]" />
            <span className="text-[10px] md:text-xs tracking-[0.3em] uppercase font-semibold">
              Parla con un Senior Associate
            </span>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-white/70 hover:text-white transition-colors"
            aria-label="Chiudi"
            data-testid="qualification-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-5 md:px-7 py-3 flex items-center gap-1.5 border-b border-border">
          {STEPS.slice(0, -1).map((_stepCfg, i) => (
            <div
              key={`dot-${i}`}
              className={`h-1 rounded-full flex-1 transition-colors ${
                i <= step ? 'bg-[var(--gp-gold)]' : 'bg-border'
              }`}
            />
          ))}
        </div>

        {/* Body */}
        <div className="px-5 md:px-7 py-6 md:py-7 max-h-[calc(100vh-220px)] overflow-y-auto">
          {loadingCfg && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
            </div>
          )}

          {!loadingCfg && config && (
            <>
              {currentStepKey === 'contact' && (
                <ContactStep form={form} setForm={setForm} />
              )}
              {currentStepKey === 'path' && (
                <PathStep config={config} form={form} setForm={setForm} />
              )}
              {currentStepKey === 'countries' && (
                <CountriesStep config={config} form={form} toggleCountry={toggleCountry} />
              )}
              {currentStepKey === 'budget' && (
                <BudgetStep form={form} setForm={setForm} budgetOptions={budgetOptions} />
              )}
              {currentStepKey === 'timeline' && (
                <TimelineStep config={config} form={form} setForm={setForm} />
              )}
              {currentStepKey === 'success' && (
                <SuccessStep
                  result={result}
                  form={form}
                  config={config}
                  budgetOptions={budgetOptions}
                />
              )}

              {error && (
                <div className="mt-4 text-sm text-red-600" data-testid="qf-error">{error}</div>
              )}
            </>
          )}
        </div>

        {/* Footer with navigation */}
        {currentStepKey !== 'success' && (
          <div className="px-5 md:px-7 py-4 border-t border-border flex items-center justify-between gap-2 bg-[var(--surface-2)]">
            <Button
              type="button"
              variant="ghost"
              onClick={back}
              disabled={step === 0}
              className="h-11"
              data-testid="qf-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Indietro
            </Button>
            {currentStepKey === 'timeline' ? (
              <Button
                type="button"
                onClick={submit}
                disabled={!canContinue || submitting}
                className="h-11 px-5 bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)] uppercase tracking-wide font-semibold"
                data-testid="qf-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Conferma e qualifica
              </Button>
            ) : (
              <Button
                type="button"
                onClick={next}
                disabled={!canContinue}
                className="h-11 px-5 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
                data-testid="qf-next"
              >
                Avanti <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>
        )}
        {currentStepKey === 'success' && (
          <div className="px-5 md:px-7 py-4 border-t border-border flex items-center justify-center bg-[var(--surface-2)]">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-11 px-6"
              data-testid="qf-close-success"
            >
              Continua a esplorare il sito
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Re-export sub-modules for callers that need them (backwards-compat).
export { COUNTRIES_BY_PATH };

export default QualificationModal;
